import { useState } from 'react'
import { knowledgeConsolidationApi } from '../../api/knowledgeConsolidation'
import { AreaBriefView } from './AreaBriefView'
import { ConsolidateButton } from './ConsolidateButton'
import { consolidationPhaseLabel, DATA_AREA_TYPES, type KnowledgeArea } from './knowledgeTypes'
import { ModuleReviewPanel } from './ModuleReviewPanel'

interface AreaDrilldownPanelProps {
  area: KnowledgeArea | null
  /** The full flat area list, used to resolve the parent path + direct children. */
  areas: KnowledgeArea[]
  projectId?: number
  canManage?: boolean
}

const STANDARD_TABS = [
  'Overview',
  'Features',
  'Requirements',
  'Business Rules',
  'Data',
  'Decisions',
  'Dependencies',
  'Conflicts',
  'Sources',
  'History',
] as const

const DATA_TABS = [
  'Overview',
  'Schema',
  'Fields',
  'Source',
  'Ingestion',
  'Transformations',
  'Consumers',
  'Quality Issues',
] as const

export function AreaDrilldownPanel({
  area,
  areas,
  projectId,
  canManage = false,
}: AreaDrilldownPanelProps) {
  const [tab, setTab] = useState<string>('Overview')
  const [refreshTick, setRefreshTick] = useState(0)

  if (!area) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-harbor-border bg-harbor-surface p-8 text-sm text-harbor-text-muted">
        Select a knowledge area to see its details.
      </div>
    )
  }

  const tabs = DATA_AREA_TYPES.has(area.areaType) ? DATA_TABS : STANDARD_TABS
  const ancestors = parseAncestors(area.path)
    .filter((id) => id !== area.id)
    .map((id) => areas.find((a) => a.id === id)?.name)
    .filter((n): n is string => Boolean(n))
  const children = areas.filter((a) => a.parentId === area.id)

  return (
    <div className="rounded-xl border border-harbor-border bg-harbor-surface">
      <div className="border-b border-harbor-border p-4">
        <div className="flex items-center gap-2">
          <span className="rounded bg-harbor-border px-1.5 py-0.5 text-[10px] uppercase text-harbor-text-secondary">
            {area.areaType}
          </span>
          <h2 className="text-lg font-semibold text-harbor-text">{area.name}</h2>
          {area.status !== 'active' && (
            <span className="text-xs text-amber-400">{area.status}</span>
          )}
          {projectId !== undefined && areas.some((a) => a.areaType === 'DOMAIN') && (
            <div className="ml-auto">
              <ConsolidateButton
                projectId={projectId}
                areaId={area.id}
                canManage={canManage}
                activeConsolidation={area.consolidation}
                onCompleted={() => setRefreshTick((t) => t + 1)}
              />
            </div>
          )}
        </div>
        {area.consolidation && (
          <p className="mt-2 text-xs text-emerald-300">
            Consolidation in progress — {consolidationPhaseLabel(area.consolidation.phase)}. This
            runs in the background; the brief updates here when it finishes.
          </p>
        )}
        {ancestors.length > 0 && (
          <p className="mt-1 text-xs text-harbor-text-muted">{ancestors.join(' › ')}</p>
        )}
        {area.aliases.length > 0 && (
          <p className="mt-1 text-xs text-harbor-text-muted">
            Also known as: {area.aliases.join(', ')}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-harbor-border px-2 pt-2">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              tab === t
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="p-4 text-sm">
        {area.consolidation?.phase === 'awaiting_module_review' &&
        area.areaType === 'DOMAIN' &&
        projectId !== undefined &&
        area.consolidation.moduleProposal ? (
          <ModuleReviewPanel
            projectId={projectId}
            runId={area.consolidation.runId}
            proposal={area.consolidation.moduleProposal as never}
            onSubmit={knowledgeConsolidationApi.submitModuleReview}
            onDone={() => setRefreshTick((t) => t + 1)}
          />
        ) : projectId !== undefined && !DATA_AREA_TYPES.has(area.areaType) && tab !== 'History' ? (
          <AreaBriefView
            projectId={projectId}
            areaId={area.id}
            tab={tab}
            refreshKey={area.briefVersion + refreshTick}
            fallbackOverview={area.description}
          />
        ) : tab === 'Overview' ? (
          <div className="space-y-3">
            <p className="text-harbor-text-secondary">
              {area.description || 'No description yet.'}
            </p>
            {children.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase text-harbor-text-muted">
                  Child areas
                </h3>
                <ul className="mt-1 list-disc pl-5 text-harbor-text-secondary">
                  {children.map((c) => (
                    <li key={c.id}>{c.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-harbor-text-muted">
            Available after you run Knowledge Consolidation on this area.
          </p>
        )}
      </div>
    </div>
  )
}

/** "/1/2/4/" → [1, 2, 4] */
function parseAncestors(path: string): number[] {
  return path
    .split('/')
    .filter(Boolean)
    .map(Number)
    .filter((n) => !Number.isNaN(n))
}
